/* eslint-disable max-lines */

import type { Segment, SegmentAgentStep, SegmentMarket, SegmentReport } from "../types"

import { COMPANY_NAME } from "../utils/branding"

export const marketColors: Record<string, string> = {
  "General Industry": "#64748B",
  Mobility: "#3B82F6",
  "Consumer & Lifestyle devices": "#8B5CF6",
  "Home & Garden devices": "#10B981",
  "Power & Communication Infrastructure": "#F59E0B",
  "Building technologies": "#14B8A6",
  "Personal care & Hygiene": "#EC4899",
  "Advanced packaging": "#F97316",
  "Aerospace & Defense": "#6366F1",
}

export const segmentMarkets: SegmentMarket[] = [
  {
    marketName: "General Industry",
    strategicRole: "Opportunistic / catch-all",
    color: marketColors["General Industry"],
  },
  {
    marketName: "Mobility",
    strategicRole: "High-growth strategic focus",
    color: marketColors.Mobility,
  },
  {
    marketName: "Consumer & Lifestyle devices",
    strategicRole: "Innovation & design-led",
    color: marketColors["Consumer & Lifestyle devices"],
  },
  {
    marketName: "Home & Garden devices",
    strategicRole: "Stable platform with pockets of growth",
    color: marketColors["Home & Garden devices"],
  },
  {
    marketName: "Power & Communication Infrastructure",
    strategicRole: "High-growth, technology-driven",
    color: marketColors["Power & Communication Infrastructure"],
  },
  {
    marketName: "Building technologies",
    strategicRole: "Energy transition & smart infrastructure",
    color: marketColors["Building technologies"],
  },
  {
    marketName: "Personal care & Hygiene",
    strategicRole: "Brand-driven, regulatory-sensitive",
    color: marketColors["Personal care & Hygiene"],
  },
  {
    marketName: "Advanced packaging",
    strategicRole: "Volume & differentiation",
    color: marketColors["Advanced packaging"],
  },
  {
    marketName: "Aerospace & Defense",
    strategicRole: "High-spec, long-cycle",
    color: marketColors["Aerospace & Defense"],
  },
]

const priorityFit: Record<string, number> = {
  "Strategic core": 9,
  "High strategic": 8.7,
  "Strategic growth": 8,
  "Accelerate growth": 7.7,
  "High volume core": 8.4,
  "Strategic niche": 7,
  "Strategic selective": 7,
  "Defend & selectively grow": 6,
  "Defend & optimize": 6,
  Defend: 5.5,
  "Platform volume": 5.8,
  "Stable platform": 5,
  "Niche innovation": 5.5,
  "Niche with growth pockets": 5.5,
  "Cash & innovation mix": 6,
  "Synergy with infra": 6.5,
  "Selective, long-term": 5,
  "Selective growth": 4.5,
  "Emerging growth": 7,
  Emerging: 6.5,
}

type RawSegment = Omit<
  Segment,
  | "marketName"
  | "strategicRole"
  | "description"
  | "attractiveness"
  | "fit"
  | "sparkline"
  | "lastRunDate"
  | "runStatus"
>

type RawMarket = {
  marketName: string
  strategicRole: string
  segments: RawSegment[]
}

const rawMarkets: RawMarket[] = [
  {
    marketName: "General Industry",
    strategicRole: "Opportunistic / catch-all",
    segments: [
      {
        id: "seg-gi-other",
        name: "General Industry (other)",
        owner: "Anders Lindgren",
        priority: "Selective growth",
        size: 450,
        cagr: 3.2,
        sales: 18,
        share: 4,
        customers: ["Helios Industrial Systems", "NordMach Components"],
        suppliers: ["MetaChem Polymers", "CoreForm Molding Tools"],
        competitors: ["UniMold Group", "IndustriFlex Solutions"],
      },
    ],
  },
  {
    marketName: "Mobility",
    strategicRole: "High-growth strategic focus",
    segments: [
      {
        id: "seg-automotive",
        name: "Automotive",
        owner: "Jonas Richter",
        priority: "Strategic core",
        size: 1200,
        cagr: 5.8,
        sales: 52,
        share: 4.3,
        customers: ["NovaDrive Automotive", "Axis Mobility Systems"],
        suppliers: ["PolyForm Resins", "DriveSeal Elastomers"],
        competitors: ["Moldex Systems", "CarTech Plastics"],
      },
      {
        id: "seg-drones-trains",
        name: "Drones, trains, scooters, airplanes, rockets, bicycles etc.",
        owner: "Jonas Richter",
        priority: "Emerging growth",
        size: 380,
        cagr: 9.5,
        sales: 11,
        share: 2.9,
        customers: ["SkyLink Flight Systems", "UrbanMotion MicroMobility"],
        suppliers: ["AeroPoly Advanced Materials", "RailForm Components"],
        competitors: ["AeroMold Technologies", "TransFlex Mobility Parts"],
      },
    ],
  },
  {
    marketName: "Consumer & Lifestyle devices",
    strategicRole: "Innovation & design-led",
    segments: [
      {
        id: "seg-headphones",
        name: "Headphone & Speakers",
        owner: "Patrick Luo",
        priority: "Defend & selectively grow",
        size: 620,
        cagr: 4.1,
        sales: 24,
        share: 3.9,
        customers: ["SonicPeak Audio", "Aurora Sound Labs"],
        suppliers: ["AudioCore Magnetics", "SoftShell Acoustics"],
        competitors: ["SoundMold Manufacturing", "AcoustiForm Plastics"],
      },
      {
        id: "seg-biometric",
        name: "Biometric measuring",
        owner: "Patrick Luo",
        priority: "Strategic growth",
        size: 410,
        cagr: 8.7,
        sales: 19,
        share: 4.6,
        customers: ["LifeTrace Wearables", "PulseSense Devices"],
        suppliers: ["BioFlex Elastomerics", "MicroSense Sensor Housings"],
        competitors: ["Meditech Enclosures", "VitaMold Precision"],
      },
      {
        id: "seg-cameras-gaming",
        name: "Digital cameras, gaming consoles, VR & AR Devices, Monitors etc.",
        owner: "Patrick Luo",
        priority: "Cash & innovation mix",
        size: 980,
        cagr: 5,
        sales: 33,
        share: 3.4,
        customers: ["VisionCore Imaging", "ImmersiPlay Gaming"],
        suppliers: ["OptiForm Lens Frames", "ThermaCase Housings"],
        competitors: ["PixelMold Solutions", "GameTech Plastics"],
      },
    ],
  },
  {
    marketName: "Home & Garden devices",
    strategicRole: "Stable platform with pockets of growth",
    segments: [
      {
        id: "seg-power-tools",
        name: "Handheld power tools",
        owner: "Luca Moretti",
        priority: "Defend",
        size: 530,
        cagr: 3.5,
        sales: 21,
        share: 4,
        customers: ["TorqueMax Tools", "GreenLine DIY"],
        suppliers: ["GripCore Polymers", "PowerShell Casings"],
        competitors: ["ToolMold Industries", "PowerCast Components"],
      },
      {
        id: "seg-coffee-machines",
        name: "Coffee machines",
        owner: "Luca Moretti",
        priority: "Niche innovation",
        size: 260,
        cagr: 4.8,
        sales: 9,
        share: 3.5,
        customers: ["Caffera Appliances", "BrewLine Systems"],
        suppliers: ["FoodGrade Polymers Ltd.", "WarmFlow Heater Parts"],
        competitors: ["BrewMold Solutions", "CafeForm Plastics"],
      },
      {
        id: "seg-refrigerators",
        name: "Refrigerators, ovens, robo cleaners, hair dryers, mowers etc.",
        owner: "Luca Moretti",
        priority: "Platform volume",
        size: 870,
        cagr: 3.9,
        sales: 28,
        share: 3.2,
        customers: ["HomeSphere Appliances", "CleanWave Robotics"],
        suppliers: ["ThermoShell Panels", "Duracase Whitegoods"],
        competitors: ["HomeTech Plastics", "ApliMold Group"],
      },
    ],
  },
  {
    marketName: "Power & Communication Infrastructure",
    strategicRole: "High-growth, technology-driven",
    segments: [
      {
        id: "seg-solar",
        name: "Solar power inverters & BEV charging",
        owner: "Marek Novak",
        priority: "Strategic core",
        size: 540,
        cagr: 11.2,
        sales: 23,
        share: 4.3,
        customers: ["SunGrid Inverters", "ChargeWay Systems"],
        suppliers: ["ElectroSeal Encapsulants", "PowerFrame Housings"],
        competitors: ["EnergiMold Solutions", "VoltCast Components"],
      },
      {
        id: "seg-grid-5g",
        name: "smart grid, batteries, 5G+ equipment, data center & cloud etc.",
        owner: "Marek Novak",
        priority: "Accelerate growth",
        size: 760,
        cagr: 10.1,
        sales: 27,
        share: 3.6,
        customers: ["GridPulse Networks", "CloudCore Systems"],
        suppliers: ["SignalShield Enclosures", "PowerCell Battery Frames"],
        competitors: ["InfraMold Technologies", "SignalCast Components"],
      },
    ],
  },
  {
    marketName: "Building technologies",
    strategicRole: "Energy transition & smart infrastructure",
    segments: [
      {
        id: "seg-smart-home",
        name: "Technical Security & smart home",
        owner: "Daniel Vogt",
        priority: "Strategic growth",
        size: 420,
        cagr: 7.4,
        sales: 16,
        share: 3.8,
        customers: ["SecureNest Systems", "HomeIQ Controls"],
        suppliers: ["SafeShell Housings", "OptiSense Sensor Frames"],
        competitors: ["SafeMold Security", "SmartCast Devices"],
      },
      {
        id: "seg-heatpumps",
        name: "Heat pumps",
        owner: "Emma Rydberg",
        priority: "High strategic",
        size: 510,
        cagr: 12,
        sales: 20,
        share: 3.9,
        customers: ["EcoTherm Systems", "HeatFlow Solutions"],
        suppliers: ["ThermaCore Plastics", "HydroSeal Components"],
        competitors: ["HeatMold Technologies", "GreenCast HVAC"],
      },
      {
        id: "seg-hvac",
        name: "HVAC, light infrastructure, elevators & escalators, Water management etc.",
        owner: "Emma Rydberg",
        priority: "Stable platform",
        size: 690,
        cagr: 3.7,
        sales: 22,
        share: 3.2,
        customers: ["FlowLine Building Systems", "VertiLift Elevators"],
        suppliers: ["HydroForm Valves", "LightShell Fixtures"],
        competitors: ["BuildMold Components", "InfraCast Systems"],
      },
    ],
  },
  {
    marketName: "Personal care & Hygiene",
    strategicRole: "Brand-driven, regulatory-sensitive",
    segments: [
      {
        id: "seg-personal-hygiene",
        name: "Personal hygiene",
        owner: "Luca Moretti",
        priority: "Strategic selective",
        size: 580,
        cagr: 4.4,
        sales: 25,
        share: 4.3,
        customers: ["PureSense Personal Care", "FreshBloom Products"],
        suppliers: ["SkinSafe Polymers", "AromaSeal Closures"],
        competitors: ["CareMold Packaging", "HygieniCast Solutions"],
      },
      {
        id: "seg-pro-hygiene",
        name: "Professional hygiene",
        owner: "Luca Moretti",
        priority: "Niche with growth pockets",
        size: 310,
        cagr: 3.9,
        sales: 12,
        share: 3.9,
        customers: ["ProClean Systems", "CliniqueCare Facilities"],
        suppliers: ["SanitaForm Components", "ProDose Dispensing"],
        competitors: ["ProHygiene Packaging", "CleanCast Devices"],
      },
    ],
  },
  {
    marketName: "Advanced packaging",
    strategicRole: "Volume & differentiation",
    segments: [
      {
        id: "seg-caps",
        name: "Caps, closures",
        owner: "Anders Lindgren",
        priority: "High volume core",
        size: 900,
        cagr: 3.3,
        sales: 48,
        share: 5.3,
        customers: ["PureLine Beverages", "NutriFlow Nutrition"],
        suppliers: ["CapCore Elastomers", "SealTech Rings"],
        competitors: ["CapMold Packaging", "ClosureCast Group"],
      },
      {
        id: "seg-lids",
        name: "Lids & Cans",
        owner: "Anders Lindgren",
        priority: "Defend & optimize",
        size: 520,
        cagr: 2.8,
        sales: 20,
        share: 3.8,
        customers: ["FreshPack Foods", "UrbanSnus Brands"],
        suppliers: ["MetalLite Shells", "FlexLid Systems"],
        competitors: ["CanMold Industries", "PackCast Solutions"],
      },
    ],
  },
  {
    marketName: "Aerospace & Defense",
    strategicRole: "High-spec, long-cycle",
    segments: [
      {
        id: "seg-radar",
        name: "Radar & lidar",
        owner: "Daniel Vogt",
        priority: "Strategic niche",
        size: 190,
        cagr: 7.9,
        sales: 6,
        share: 3.2,
        customers: ["SkyScan Systems", "TerraWave Sensing"],
        suppliers: ["ShieldCore EMC Housings", "OptiBeam Windows"],
        competitors: ["AeroMold Defense", "SignalCast Enclosures"],
      },
      {
        id: "seg-aerospace",
        name: "Commercial Aerospace",
        owner: "Jonas Richter",
        priority: "Emerging",
        size: 260,
        cagr: 6.1,
        sales: 7,
        share: 2.7,
        customers: ["SkyLine Aerospace", "OrbitalFlight Systems"],
        suppliers: ["AeroGrade Polymers", "LightFrame Interior Panels"],
        competitors: ["FlightMold Components", "AviCast Interiors"],
      },
      {
        id: "seg-defense",
        name: "Defense",
        owner: "Jonas Richter",
        priority: "Selective, long-term",
        size: 300,
        cagr: 5,
        sales: 8,
        share: 2.7,
        customers: ["FortiDefense Systems", "ShieldLine Technologies"],
        suppliers: ["ArmorPoly Composites", "SecureSeal Components"],
        competitors: ["DefenMold Solutions", "MilCast Systems"],
      },
      {
        id: "seg-comm",
        name: "Communication",
        owner: "Marek Novak",
        priority: "Synergy with infra",
        size: 210,
        cagr: 6.8,
        sales: 7,
        share: 3.3,
        customers: ["BattleCom Systems", "FieldWave Comms"],
        suppliers: ["SignalShell Housings", "SecureLink Connectors"],
        competitors: ["ComMold Defense", "RadioCast Enclosures"],
      },
    ],
  },
]

function pseudoSeries(seed: number, trend: number, count = 12): number[] {
  let x = seed
  const out: number[] = []
  let value = 100

  for (let i = 0; i < count; i += 1) {
    x = (x * 9301 + 49297) % 233280
    const noise = (x / 233280 - 0.5) * 8
    value = value * (1 + trend / 100 / 12) + noise
    out.push(Math.round(value * 10) / 10)
  }

  return out
}

export const mockSegments: Segment[] = rawMarkets.flatMap((market) =>
  market.segments.map((segment) => {
    const attractiveness = Math.min(
      10,
      Math.max(
        1,
        4 + (segment.cagr - 3) * 0.55 + Math.log10(Math.max(segment.size, 50) / 100) * 0.9
      )
    )
    const fitBase = priorityFit[segment.priority ?? ""] ?? 5.5
    const fit = Math.min(10, Math.max(1, fitBase + (segment.share - 3.5) * 0.35))
    const seed = segment.id.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0)

    return {
      ...segment,
      marketName: market.marketName,
      strategicRole: market.strategicRole,
      description: `${segment.name} sits in ${market.marketName} with ${market.strategicRole.toLowerCase()} relevance.`,
      attractiveness: Math.round(attractiveness * 10) / 10,
      fit: Math.round(fit * 10) / 10,
      // Illustrative demo addressable split (two product lines), derived from market
      // size with a per-segment variation so the figures look realistic in the demo.
      // Real tenants get these as imported master data (§G1) — not from this formula.
      addressable: {
        plastics: Math.round(segment.size * (0.06 + (seed % 5) * 0.006)),
        boxBuild: Math.round(segment.size * (0.02 + (seed % 4) * 0.003)),
      },
      sparkline: pseudoSeries(seed, segment.cagr),
      lastRunDate: "2026-05-01",
      runStatus: segment.cagr > 6 ? "needs-review" : "ok",
    }
  })
)

export const mockSegmentReports: Record<string, SegmentReport> = {
  "seg-coffee-machines": {
    runId: "run-2026-05-01",
    generatedAt: "2026-05-01T10:30:00Z",
    periodCovered: { from: "2026-04-01", to: "2026-04-30" },
    summary: {
      executiveSummary: [
        "Coffee machines market continues steady mid-single-digit growth, with premiumization driving margin expansion in espresso and single-serve.",
        "Two of the three top customers signaled capacity expansion; combined order pipeline is up about 12 percent versus prior quarter.",
        "A new eco-design competitor entered the EU market with sub-2kg energy footprint claims.",
        "Subscription-based coffee supply models accelerate; OEMs increasingly require IoT-enabled housings.",
        "Recommended: validate top-5 prospects, fast-track eco-design positioning study.",
      ],
      keyChangesSinceLastRun: [
        "New competitor product launch in EU with eco-design positioning.",
        "Market growth estimate revised from 4.0 percent to 4.8 percent CAGR 5y.",
        "Customer A announced capacity expansion in EU.",
      ],
    },
    marketOverview: {
      marketSize: {
        value: 260,
        unit: "MEUR",
        confidence: "medium",
        sources: ["example-industry-site.com/article-123", "brewline-trends.com/2026-q1"],
      },
      marketGrowth: {
        cagr3yPct: 4.8,
        cagr5yPct: 4.5,
        confidence: "medium",
        sources: ["example-newsletter.com/market-outlook"],
      },
      keyApplications: [
        "Home espresso machines",
        "Office coffee systems",
        "Pod & capsule machines",
        "Bean-to-cup professional",
      ],
      macroTrends: [
        "Shift to premium single-serve devices.",
        "Increased demand for energy-efficient appliances.",
        "Sustainability claims becoming a purchase criterion in EU markets.",
      ],
      regulatoryFactors: [
        "Energy efficiency labeling changes under discussion under EU Ecodesign revision.",
        "PFAS restrictions affecting heating element coatings require monitoring.",
      ],
      sections: {
        keyApplications: { confidence: "high", sources: ["example-industry-site.com/article-123"] },
        macroTrends: { confidence: "medium", sources: ["example-newsletter.com/market-outlook"] },
        regulatoryFactors: {
          confidence: "low",
          sources: ["eur-lex.europa.eu/ecodesign-2026", "echa.europa.eu/pfas-restriction"],
        },
      },
    },
    customerAnalysis: {
      existingKeyCustomers: [
        {
          name: "Caffera Appliances",
          changeSummary: "Announced capacity expansion in EU; new line in Northern Italy.",
          flag: "NEW MOVE",
        },
        {
          name: "BrewLine Systems",
          changeSummary: "No major news in this period; steady reorder cadence.",
        },
      ],
      potentialNewCustomers: [
        {
          name: "Tavola Espresso Group",
          rationale: `Similar product portfolio; active in ${COMPANY_NAME} target geographies; growing premium share.`,
        },
        {
          name: "NorthBrew Appliances",
          rationale: "Growing share in premium single-serve segment; sourcing from Asia today.",
        },
        {
          name: "AromaCraft Devices",
          rationale:
            "Emerging brand with strong direct-to-consumer traction; needs scalable molding partner.",
        },
      ],
    },
    competitorAnalysis: {
      mainCompetitors: [
        {
          name: "BrewMold Solutions",
          changeSummary: "Introduced new eco-design product line; pitched to mid-tier OEMs.",
          flag: "NEW MOVE",
        },
        {
          name: "CafeForm Plastics",
          changeSummary: "Opened a new plant in Asia; capacity up about 20 percent.",
        },
      ],
      newEntrants: [
        {
          name: "Verde Mould Co.",
          rationale:
            "Startup focusing on bio-based polymers for coffee housings; pitched at K-Show.",
        },
      ],
      positionVsCompetition: {
        strengths: [
          "Strong track record in complex plastic components.",
          "Established quality systems for food-contact materials.",
        ],
        weaknesses: [
          "Limited presence in high-end premium brands.",
          "Mid-tier eco-positioning still developing.",
        ],
      },
    },
    trendAndInnovation: {
      technologyTrends: [
        "Integration of IoT and app-based control.",
        "Sustainable materials focus in housings, including recycled and bio-based polymers.",
        "Compact thermoblock heating systems replacing boiler designs.",
      ],
      businessModelTrends: [
        "Subscription-based coffee supply models.",
        "Trade-in and refurb programs from premium OEMs.",
      ],
    },
    quantitativeIndicators: {
      companySharePct: 3.5,
      companyShareComment: "Approximate share based on public benchmarks.",
      pricePressureIndicator: "medium",
      growthAttractivenessScore: 7.5,
      strategicFitScore: 6.8,
    },
    risksAndOpportunities: {
      risks: [
        "High sensitivity to consumer confidence and discretionary spending.",
        "Energy-cost driven cycle in commercial coffee equipment.",
      ],
      opportunities: [
        "Premiumization and energy efficiency regulations support the upgrade cycle.",
        "Subscription and IoT models open recurring-revenue conversations with OEMs.",
      ],
    },
    recommendedActions: {
      forSegmentOwner: [
        "Validate list of potential new customers and prioritize top 5 by Q3.",
        "Review positioning vs new eco-design competitor BrewMold.",
        "Brief technical team on PFAS-free heating element requirements.",
      ],
      forGlobalSalesAdmin: [
        "Consider allocating additional BD resources to premium subsegment.",
        "Evaluate need for deeper paid research if uncertainty around eco-design remains high.",
        "Align with Heat pumps owner on shared customers in EU appliances.",
      ],
    },
    history: [
      {
        runId: "run-2026-04-01",
        generatedAt: "2026-04-01T09:00:00Z",
        changes: ["Baseline report created."],
      },
      {
        runId: "run-2026-05-01",
        generatedAt: "2026-05-01T10:30:00Z",
        changes: [
          "Market growth revised; new entrant identified.",
          "BrewMold eco-design line flagged.",
          "3 prospect candidates added.",
        ],
      },
    ],
  },
}

export const segmentAgentScript: SegmentAgentStep[] = [
  {
    id: "market_agent",
    name: "Market agent",
    icon: "M",
    messages: [
      "Connecting to priority sources...",
      "Fetching market size & growth signals...",
      "Cross-checking numeric fields...",
      "Extracting macro trends and regulation.",
    ],
    durationMs: 1800,
  },
  {
    id: "customer_agent",
    name: "Customer agent",
    icon: "C",
    messages: [
      "Loading existing customer list...",
      "Scanning customer news in last 30 days...",
      "Searching for similar prospects via keywords...",
      "Ranking 12 candidates by similarity.",
    ],
    durationMs: 1600,
  },
  {
    id: "competitor_agent",
    name: "Competitor agent",
    icon: "K",
    messages: [
      "Monitoring incumbent competitors...",
      "Detecting product launches & plant moves...",
      "Scanning for new entrants and startups...",
      "Compiling position-vs-competition view.",
    ],
    durationMs: 1600,
  },
  {
    id: "consolidation_agent",
    name: "Consolidation agent",
    icon: "S",
    messages: [
      "Merging agent outputs...",
      "Computing attractiveness & fit scores...",
      "Drafting recommended actions...",
      "Finalizing standardized segment plan.",
    ],
    durationMs: 1400,
  },
]
